import { houseLabel } from '@auctisync/api-client';
import { useEffect, useState, type ReactNode } from 'react';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { supabase } from '../../lib/supabase';

/**
 * The auth split — brand panel beside the form.
 *
 * The phone had to CHOOSE between showing the reason to sign in and the act of
 * signing in. The web shows both, which matters most on the page where somebody
 * decides whether this site is worth an account.
 *
 * 🔴 A HOUSE BUILD NAMES ITS LIVE SALE; the AuctiSync build cannot and says
 * something honest instead. Inventing "218 lots open" for a surface that does
 * not know whose sale it is would be the one lie on the page asking for trust.
 * The sale line only appears when a house is actually known AND has an open
 * sale — otherwise the panel drops it rather than showing an empty pill.
 *
 * THE THREE PROOF POINTS ARE THE APP'S FIRST-RUN FACTS. Same three, same
 * order, same claims — maximum, timer extension, and that a card is not a
 * charge. A bidder who learns them on a phone must not meet different ones here.
 *
 * Nav and footer are suppressed around these pages (see `PublicLayout`): a
 * half-signed-in visitor being offered "Your bidding" is an invitation to a
 * page that will bounce them back here.
 */
export function AuthSplit({ children }: { children: ReactNode }) {
  const { active } = useBidderHouses();
  const [openSale, setOpenSale] = useState<{ title: string; lots: number } | null>(null);

  const tenantId = active?.tenant_id ?? null;
  useEffect(() => {
    if (tenantId === null) {
      setOpenSale(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('auctions')
        .select('title, lot_count')
        .eq('tenant_id', tenantId)
        .eq('is_visible', true)
        .eq('status', 'active')
        .order('ends_at', { ascending: true, nullsFirst: false })
        .limit(1);
      if (cancelled) return;
      const sale = (data ?? [])[0];
      setOpenSale(sale ? { title: sale.title, lots: sale.lot_count } : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const house = active === null ? null : houseLabel(active);
  const logo =
    active?.branding && typeof active.branding['logo_url'] === 'string'
      ? active.branding['logo_url']
      : null;

  return (
    <div className="wau">
      <div className="wau-brand">
        {logo !== null && logo !== '' ? (
          <img
            src={logo}
            alt={house ?? 'AuctiSync'}
            style={{ height: 44, width: 'auto', alignSelf: 'flex-start', flex: '0 0 auto' }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              className="w-plate"
              style={{ background: 'var(--text-on-deep)', color: 'var(--brand-primary)' }}
              aria-hidden
            >
              {(house ?? 'AuctiSync').charAt(0).toUpperCase()}
            </span>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700 }}>
              {house ?? 'AuctiSync'}
            </span>
          </div>
        )}

        <div>
          {openSale !== null ? (
            <span className="wau-live">
              <span className="w-dot" style={{ background: 'var(--semantic-live)' }} />
              {openSale.title} · {openSale.lots} lot{openSale.lots === 1 ? '' : 's'}
            </span>
          ) : null}
          <h1
            className="w-h1"
            style={{ fontSize: 38, color: 'var(--text-on-deep)', marginTop: 16 }}
          >
            Bid from anywhere
          </h1>
          <p
            className="w-body"
            style={{ marginTop: 12, maxWidth: 420, color: 'var(--text-label-on-deep)' }}
          >
            {house === null
              ? 'One account, every auction house on the platform. We take you to yours.'
              : 'Watch lots, set a maximum, and pay for what you win — without standing in the yard all day.'}
          </p>
        </div>

        <div className="wau-proof">
          {[
            [
              'Your maximum stays secret',
              'We bid the least needed to keep you in front. Nobody sees your limit — not the house, not other bidders.',
            ],
            [
              'Late bids push the clock',
              'A bid in the closing window extends that lot. Nothing is won by sniping the last second.',
            ],
            [
              'Nothing is charged until you win',
              'Cards are verified once, for a dollar, and released. Invoices come after the sale.',
            ],
          ].map(([heading, body]) => (
            <div key={heading}>
              <span aria-hidden style={{ opacity: 0.8, marginTop: 1 }}>
                ●
              </span>
              <span>
                <b
                  style={{
                    display: 'block',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 650,
                  }}
                >
                  {heading}
                </b>
                <span style={{ color: 'var(--text-label-on-deep)', fontSize: 13.5 }}>{body}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="wau-pane">
        <div className="wau-form">{children}</div>
      </div>
    </div>
  );
}
